import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FilterTypeCheckboxModel } from '../../models/filter-type-checkbox.model';
import { FilterOptionModel } from '../../models/filter-option.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AbstractFilterTypeComponent } from '../../models/abstract-filter-type.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { CheckboxTypesEnum } from '../../../Forms/enums/checkbox-types.enum';
import { IncludeExcludeEnum } from '../../enums/include-exclude.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';


@Component({
    selector: 'agpr-filter-checkboxes',
    templateUrl: './filter-checkboxes.component.html',
    styleUrls: ['./filter-checkboxes.component.scss'],
    providers: [
        {
            provide: AbstractFilterTypeComponent,
            useExisting: FilterCheckboxesComponent
        }
    ],
    standalone: false
})
export class FilterCheckboxesComponent extends AbstractFilterTypeComponent implements OnChanges, AfterViewInit {
    @Input() declare filter: FilterTypeCheckboxModel;

    filteredOptions: FilterOptionModel[];
    value: any[];
    anyOptionsSelected: boolean = false;
    searchQuery: string;
    showAddCustomButton: boolean = false;

    readonly buttonTypes = ButtonTypesEnum;
    readonly severity = messageSeverityEnum;
    readonly checkboxTypes = CheckboxTypesEnum;
    readonly includeExcludeEnum = IncludeExcludeEnum;

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onChange: EventEmitter<any> = new EventEmitter();

    ngOnChanges(): void {
        this.searchQuery = '';
        this.refresh();
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.scrollPanel?.sp?.moveBar();
        });
    }

    /**
     * Refreshes the current value of the filter
     */
    refresh(): void {
        this.search();
        this.value = this.filter.getCurrentValue();
        this.anyOptionsSelected = this.value.length > 0;
    }

    /**
     * Updates the current value of the filter
     */
    handleChange(): void {
        this.filter.setCurrentValue(this.value);
        this.anyOptionsSelected = this.value.length > 0;
        this.onChange.emit(this.value);
    }

    selectItems(): void {
        if (!this.anyOptionsSelected && this.filteredOptions.length > 0 ) {

            /**
             * Selects all of the options
             */
            this.value = this.filteredOptions.map((option) => option.value);
            this.handleChange();
        } else {

            /**
             * Deselects all of the options
             */

            this.value = [];
            this.handleChange();
        }
    }

    /**
     * Filters the visible options based on the search query
     */
    search(): void {
        this.filteredOptions = this.filter.options.slice();
        this.showAddCustomButton = false;
        if (this.searchQuery) {
            let hasExactMatch = false;
            const queryLower = this.searchQuery.toLocaleLowerCase();
            this.filteredOptions = this.filteredOptions.filter(option => {
                const optionLower = option.label.toLocaleLowerCase();

                if (optionLower === queryLower) {
                    hasExactMatch = true;
                    return true;
                }

                return optionLower.includes(queryLower);
            });

            if (this.filter.addCustom && !hasExactMatch) {
                this.showAddCustomButton = true;
            }
        }
    }

    /**
     * Adds a custom option as defined by the user's current search query
     */
    addCustom(): void {
        const adder = this.filter.addCustom;

        if (!adder) {
            return;
        }

        const item = adder(this.searchQuery);
        this.searchQuery = '';
        this.showAddCustomButton = false;

        if (!item) {
            this.refresh();
            return;
        }

        this.filter.options = [
            ...this.filter.options,
            item
        ];
        this.filter.setCurrentValue([
            ...this.value,
            item.value
        ]);
        this.refresh();
    }

    handleInputKeydown(event: KeyboardEvent) {
        // when there is no match in the filtered list and push "enter" key will add the customized filter keyword
        if(event.key === ActionKeys.ENTER && this.showAddCustomButton){
            this.addCustom();
        }
    }
}
